<template>
  <FullPageLayout>
    <template v-if="fetchState.pending">
      <div>loading...</div>
    </template>
    <template v-else-if="fetchState.error">
      <ErrorPageLayout :error="fetchState.error" />
    </template>
    <template v-else>
      <AdController unit="masthead" />
      <div class="tag-wrapper mx-auto py-[25px] px-0 w-full sm:w-[1100px]">
        <OBreadcrumb class="mb-4 mx-4 sm:mx-0" :items="[{ href: tagData.route, name: tagData.name }]" />
        <div class="sm:mb-5 mb-6 mx-4 sm:mx-0">
          <h1 class="font-bold primary-100 mb-6 text-24">{{ tagData.headline || tagData.name }} {{ $t('main.news-post') }}</h1>
          <p class="font-normal text-[15px]">{{ tagData.description }}</p>
        </div>
        <div v-if="imageUrl" class="flex space-y-6 sm:space-y-0 sm:space-x-6 flex-wrap sm:flex-nowrap">
          <TagInfoCard :data="tagData" />
          <div class="flex flex-row flex-wrap w-full tag-dual-columns space-y-6 sm:space-y-0">
            <template v-for="(item, index) in tagFeed.slice(0, 3)">
              <TagSingleCard :key="item._id" :article="item" />
              <AdController
                v-if="!isSponsored"
                :key="`labelsDetailTopAd_${index}`"
                :parent-index="index"
                unit="labelsDetailTop"
                class="sm:w-[350px] bg-white sm:rounded-md article-card w-full u-center"
              />
            </template>
          </div>
        </div>
        <div
          class="flex flex-wrap mt-6 space-y-6 sm:space-y-0"
          :class="getArticlesFirstColumn > 2 ? 'tag-horizontal-columns' : 'tag-ternate-columns'"
        >
          <template v-for="(item, index) in getArticlesFirstColumn">
            <TagSingleCard :key="item._id" :article="item" :data-layer="{ name: `Etiket - ${tagData.name} - Tümü`, position: index + 1 }" />
            <AdController
              v-if="!isSponsored"
              :key="`labelsTopAd_${index}`"
              :parent-index="index"
              unit="labelsTop"
              class="sm:w-[350px] w-full bg-white sm:rounded-md article-card u-center"
            />
          </template>
        </div>
        <div class="flex items-center flex-nowrap my-7 mx-4 sm:mx-0">
          <OIcon icon="trend-arrow" svg-class="w-7 h-7 primary-100" />
          <h3 class="mx-2 font-medium leading-18 primary-100 whitespace-nowrap" :class="isMobile ? 'text-22' : 'text-24'">
            {{ $t('tag.popular') }}
          </h3>
          <div class="border border-[#E2E5ED] w-full"></div>
        </div>
        <ArticleSlider
          v-if="popular.length"
          :articles="popular"
          class="px-4 sm:px-0"
          arrow-color="#8197af"
          :data-layer="{ name: `Etiket Liste - Slider` }"
          :class="`${isMobile ? 'mb-12' : 'mb-14'}`"
        >
          <SwiperSlide v-for="article in popular" :key="article._id">
            <CarouselArticle :article="article" variant="tag" />
          </SwiperSlide>
        </ArticleSlider>
        <div class="flex flex-wrap mt-4 tag-ternate-columns space-y-6 sm:space-y-0">
          <template v-for="(item, index) in getArticlesSecondColumn">
            <AdController
              v-if="index === 5 && !isSponsored"
              :key="`labelsAd_${index}`"
              unit="labelsMiddle1Desktop"
              class="sm:w-[350px] bg-white sm:rounded-md article-card u-center w-full"
            />
            <TagSingleCard
              :key="item._id"
              :article="item"
              :data-layer="{ name: `Etiket - ${tagData.name} - Tümü`, position: index + getArticlesFirstColumn.length + 1 }"
            />
            <AdController
              v-if="!tagData.detail && !isSponsored"
              :key="`labelsAd_${index}`"
              :parent-index="index"
              unit="labelsMiddle2"
              class="sm:w-[350px] bg-white sm:rounded-md article-card u-center w-full"
            />
            <AdController
              v-if="tagData.detail && !isSponsored"
              :key="`labelsAd_${index}`"
              :parent-index="index"
              unit="labelsMiddle2Detail"
              class="sm:w-[350px] bg-white sm:rounded-md article-card u-center w-full"
            />
            <AdController
              v-if="tagData.detail && index === getArticlesSecondColumn.length - 1 && !isSponsored"
              :key="`labelsAd_${index}`"
              unit="labelsMiddle2End"
              class="sm:w-[350px] bg-white sm:rounded-md article-card u-center w-full"
            />
          </template>
        </div>
        <OPaginationBase class="text-center mb-7.5 mt-6" :current-page="pageNumber" :has-next-page="tagPagination.hasNextPage" />
        <AdController unit="taboola" />
      </div>
      <AdController unit="bodyBottom" />
    </template>
  </FullPageLayout>
</template>

<script>
import FullPageLayout from '@/modules/common/components/layout/FullPageLayout';
import AdController from '@/modules/ads/components/AdController';
import { useTagMeta } from '@/modules/tag/utils/tagMeta';
import { defineComponent, useContext, useFetch, ref, computed, ssrRef } from '@nuxtjs/composition-api';
import { useDevice } from '@/composables/useDevice';
import { initializeAdLoaders } from '@/modules/ads/adUtils';
import { useTracking } from '@/modules/tracking/composables/useTracking';
import ErrorPageLayout from '@/modules/common/components/layout/ErrorPageLayout.vue';
import { OBreadcrumb, OIcon } from '@/modules/common/components/ui';
import SwiperSlide from '@/modules/sliders/core/SwiperSlide';
import { getImageUrl } from '~/modules/common/utils/imageUtils';
import TagSingleCard from '~/modules/card/templates/tag/TagArticleCard';
import OPaginationBase from '~/modules/common/components/ui/OPagination/OPaginationBase';
import TagInfoCard from '~/modules/tag/components/TagInfoCard';
import CarouselArticle from '~/modules/card/templates/common/CarouselArticle';
import ArticleSlider from '~/modules/sliders/components/common/ArticleSlider';

export default defineComponent({
  components: {
    FullPageLayout,
    OPaginationBase,
    AdController,
    TagSingleCard,
    TagInfoCard,
    CarouselArticle,
    ErrorPageLayout,
    OBreadcrumb,
    ArticleSlider,
    SwiperSlide,
    OIcon,
  },
  head: {
    bodyAttrs: {
      class: 'bg-[#f7f8fb] o-ps-global',
    },
  },
  setup() {
    const { $service, route, $error, $cache } = useContext();
    const { generateAdConfig } = initializeAdLoaders();
    const serversideAdConfig = generateAdConfig('Tag');
    const { isMobile } = useDevice();
    const tagName = route.value.params.pathMatch;
    const pageNumber = parseInt(route.value.params.pageNum) || 1;
    const tagData = ref({});
    const tagFeed = ref([]);
    const tagPagination = ref({});
    const popular = ref({});
    const isSponsored = ssrRef(false);
    $cache('public, max-age=30');
    const { fetch, fetchState } = useFetch(async () => {
      try {
        const [{ data, pagination }, { data: tagInfo }, { data: popularFeed }] = await Promise.all([
          $service.tag.getTagFeed(tagName, pageNumber, 21),
          $service.tag.getTagInfo(tagName),
          $service.tag.getTagPopularFeed(tagName),
        ]);
        tagData.value = tagInfo;
        tagFeed.value = data;
        tagPagination.value = pagination;
        if (tagFeed.value.length === 0 && !tagPagination.value?.hasNextPage) {
          $error('page not found', 404, false);
        }
        popular.value = popularFeed;
        const adConfig = serversideAdConfig(tagData);
        isSponsored.value = adConfig.value?.decidedMode?.value === 'sponsored';
      } catch (error) {
        $error(error);
      }
    });
    const imageUrl = computed(() => getImageUrl(tagData?.value?.detail?.image, { width: 225, standard: true }) || '');
    const getArticlesFirstColumn = computed(() => {
      if (isSponsored.value) {
        return tagFeed.value.slice(...(imageUrl.value ? [4, 7] : [0, 9]));
      } else {
        return tagFeed.value.slice(...(imageUrl.value ? [3, 6] : [0, 8]));
      }
    });
    const getArticlesSecondColumn = computed(() => {
      if (isSponsored.value) {
        return tagFeed.value.slice(...(imageUrl.value ? [7, 21] : [9, 21]));
      } else {
        return tagFeed.value.slice(...(imageUrl.value ? [6, 21] : [8, 21]));
      }
    });
    useTagMeta({ data: tagData, feed: tagFeed, pagination: tagPagination });
    // ------- Google Pageview Event Beginning -------
    const { sendPageviewEvent } = useTracking();
    sendPageviewEvent(
      () => ({
        page: {
          type: 'Etiket Liste',
        },
        content: {
          tags: [tagData.value.name],
        },
      }),
      [tagData]
    );
    // ------- Google Pageview Event End ----------
    return {
      tagData,
      tagFeed,
      fetch,
      fetchState,
      popular,
      pageNumber,
      tagPagination,
      getArticlesFirstColumn,
      imageUrl,
      getArticlesSecondColumn,
      isMobile,
      isSponsored,
    };
  },
});
</script>
<style lang="scss">
.tag-wrapper {
  h1 {
    line-height: 29px;
    margin: 10px 0;
  }
  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #424242;
    margin: 10px 0;
  }
  p {
    margin: 10px 0;
    color: #424242;
    font-size: 15px;
    line-height: 24px;
    font-weight: 400;
  }

  @media (min-width: 640px) {
    .tag-dual-columns {
      .article-card {
        &:nth-child(-n + 2) {
          margin-bottom: 24px;
        }
        &:nth-child(2n) {
          margin-left: 24px;
        }
      }
    }

    .tag-horizontal-columns {
      .article-card {
        &:nth-child(n + 2) {
          margin-left: 24px;
        }
      }
    }

    .tag-ternate-columns {
      .article-card {
        margin-bottom: 24px;
        &:nth-child(3n-3),
        &:nth-child(3n-4) {
          margin-left: 24px;
        }
      }
    }
  }

  .categoryCarouselMobileDiv {
    @media (max-width: 384px) {
      padding-right: 8px !important;
    }
    @media (max-width: 375px) {
      padding-right: 0px !important;
    }
  }
}
</style>
